<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name: 'sketch', position: 'top', allowOtherLeft: true }"
    :button-bind="{ icon, tooltip }"
    :loading="loading"
    :target="target"
    unfill-width
  >
    <template #content>
      <div class="tw:p-2.5 tw:flexrow-1.5 tw:items-stretch">
        <div class="tw:flexcenter">
          <!-- DRAWING BUTTON -->
          <base-button
            icon="mdi-brush"
            type="text"
            :color="active ? 'primary' : undefined"
            :disabled="!visible"
            :tooltip="$i18n.t(`navbar.sketch.turn${active ? 'Off' : 'On'}Drawing`)"
            @click="active = !active"
          />
        </div>

        <!-- COLOR PICKER -->
        <div class="tw:flexcenter">
          <v-tooltip color="font" bottom>
            <template #activator="{ on }">
              <div v-on="on">
                <data-input v-model="color" :data-type="{ name: 'color' }" />
              </div>
            </template>
            {{ $i18n.t('navbar.sketch.color') }}
          </v-tooltip>
        </div>

        <!-- STROKE WIDTH NUMERIC INPUT -->
        <div class="tw:flexcenter">
          <div class="tw:max-w-[50px]">
            <v-tooltip color="font" bottom>
              <template #activator="{ on }">
                <div v-on="on">
                  <data-input
                    :value="stroke"
                    @change="$event => (stroke = parseInt($event))"
                    :data-type="{ name: 'integer' }"
                    class="tw:h-[25px] tw:[&_.v-input\_\_slot]:h-[25px] tw:[&_.v-input\_\_slot]:min-h-[25px]"
                    :min-value="1"
                    :max-value="10"
                    hide-arrows
                    hide-details
                  />
                </div>
              </template>
              {{ $i18n.t('navbar.sketch.stroke') }}
            </v-tooltip>
          </div>
        </div>

        <!-- STROKE WIDTH SLIDER INPUT -->
        <div class="tw:flexcenter">
          <v-tooltip color="font" bottom>
            <template #activator="{ on }">
              <div v-on="on" class="tw:max-w-[150px] tw:w-[150px] tw:flexcenter">
                <data-input
                  v-model="stroke"
                  class="tw:grow tw:[&_>_div_>_div]:pt-0 tw:[&_.v-input\_\_slot]:p-0"
                  :data-type="{ name: 'slider' }"
                  :min="1"
                  :max="10"
                  hide-value-label
                  hide-details
                />
              </div>
            </template>
            {{ $i18n.t('navbar.sketch.stroke') }}
          </v-tooltip>
        </div>

        <!-- DIVIDER -->
        <div class="tw:bg-gray-200 tw:w-[1px]"></div>

        <!-- ACTION BUTTONS -->
        <div class="tw:flexcenter">
          <!-- VISIBILITY -->
          <base-button
            type="text"
            :color="visible ? 'primary' : undefined"
            :icon="visible ? 'mdi-eye' : 'mdi-eye-off'"
            :loading="refreshingTarget === 'visibility'"
            :disabled="refreshingTarget && refreshingTarget !== 'visibility'"
            :tooltip="$i18n.t(`navbar.sketch.${visible ? 'hide' : 'show'}Sketches`)"
            @click="visible = !visible"
          />

          <!-- REFRESH -->
          <base-button
            type="text"
            icon="mdi-refresh"
            :loading="refreshingTarget === 'refreshing'"
            :disabled="refreshingTarget && refreshingTarget !== 'refreshing'"
            :tooltip="$i18n.t('navbar.sketch.refresh')"
            @click="redrawSketches('refreshing')"
          />

          <!-- MENU CLOSE -->
          <base-button
            type="text"
            icon="mdi-close"
            :tooltip="$i18n.t('navbar.sketch.close')"
            @click="isMenuOpen = false"
          />
        </div>
      </div>

      <!-- MAP POPOVER -->
      <sketch-popover
        v-if="identificationData"
        :coordinates="identificationData?.coordinates"
        :feature="identificationData?.feature"
        :target="target"
        @onRemoved="identificationData = null"
      />
    </template>
  </navbar-menu-tool>
</template>

<script>
import NavbarMenuTool from '@/components/NavbarMenuTool';
import DataInput from '@/components/DataInput';
import SketchPopover from '@/components/SketchPopover';

import sketchContext from '@/mixins/sketchContext.js';

export default {
  name: 'TheNavbarToolsSketch',
  mixins: [sketchContext],
  components: {
    NavbarMenuTool,
    DataInput,
    SketchPopover,
  },
  props: {
    icon: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
    target: {
      type: String,
    },
  },
  data: () => ({
    isMenuOpen: false,
    loading: false,

    active: true,
    visible: true,
    refreshingTarget: null,

    color: '#000',
    stroke: 2,

    drawings: [],

    identificationData: null,
  }),
  computed: {
    style() {
      return { color: this.color, stroke: this.stroke };
    },
  },
  watch: {
    /** Save currently changed style to localstorage */
    style(style) {
      localStorage.setItem('sketches-brush-color', style.color);
      localStorage.setItem('sketches-brush-stroke', style.stroke);
      this.$root.$emit('changeSketchStyle', style, this.createContextSketch);
    },

    /** Action on menu close and open */
    isMenuOpen(isOpened) {
      if (isOpened) {
        this.switchActiveTool();
      } else {
        this.identificationData = null;
        this.$root.$emit('sketch-action', false);
        this.$root.$emit('sketchIdentification-action', false);
      }
    },

    /** Visibility manipulation */
    visible(isVisible) {
      if (!this.isMenuOpen) return;
      this.setStoreVisibilityPerContext();
      if (isVisible) {
        if (!this.active) this.$root.$emit('sketchIdentification-action', true, this.onSketchIdentified);
        this.redrawSketches('visibility');
      } else {
        this.active = false;
        this.identificationData = null;
        this.$root.$emit('toggleSketchLayer');
      }
    },

    /** Drawing state manipulation */
    active(isActive) {
      if (!this.isMenuOpen) return;
      if (isActive) localStorage.removeItem('sketches-drawing-deactivated');
      else localStorage.setItem('sketches-drawing-deactivated', 'true');
      this.switchActiveTool();
    },
  },
  methods: {
    async redrawSketches(target) {
      this.refreshingTarget = target ?? 'none';
      this.identificationData = null;
      try {
        this.drawings = await this.getContextSketches();
        this.$root.$emit('toggleSketchLayer', this.drawings ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        this.refreshingTarget = null;
      }
    },

    /** If menu is opened and sketches are visible on the map, there should be either identification or drawing enabled */
    switchActiveTool() {
      this.identificationData = null;
      if (this.active) {
        this.$root.$emit(
          'sketch-action',
          true,
          {
            color: this.color,
            stroke: this.stroke,
          },
          this.createContextSketch
        );
      } else if (this.visible) {
        this.$root.$emit('sketchIdentification-action', true, this.onSketchIdentified);
      } else {
        this.$root.$emit('sketch-action', false);
        this.$root.$emit('sketchIdentification-action', false);
      }
    },
    onSketchIdentified(data) {
      this.identificationData = data.feature ? data : null;
    },

    /** Initial logic */
    setInitialVisibility() {
      this.visible = this.areSketchesVisiblePerContext();
      if (this.visible) this.redrawSketches();
    },
    setInitialDrawingState() {
      const drawingDeactivated = !!localStorage.getItem('sketches-drawing-deactivated');
      this.active = drawingDeactivated ? false : !!this.visible;
    },
    setInitialStyle() {
      const brushColor = localStorage.getItem('sketches-brush-color');
      if (brushColor) this.color = brushColor;
      const brushStroke = parseInt(localStorage.getItem('sketches-brush-stroke'));
      if (brushStroke) this.stroke = brushStroke;
    },
  },
  beforeDestroy() {
    this.$root.$emit('toggleSketchLayer');
    this.$root.$emit('sketch-action', false);
    this.$root.$emit('sketchIdentification-action', false);
  },
  created() {
    /** Get visibility from localstorage and parse drawings if it is not turned off */
    this.setInitialVisibility();

    /** Get drawing active state from localstorage  */
    this.setInitialDrawingState();

    /** Get draw parameters from localstorage */
    this.setInitialStyle();
  },
};
</script>
